import { createClient } from '@supabase/supabase-js'
import { ITEM_TERMINAL_STATUSES, UNIT_TERMINAL_STATUSES } from './utils/statusConstants'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Missing Supabase environment variables!')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    storageKey: 'exitium-auth',
    storage: typeof window !== 'undefined' ? window.localStorage : undefined,
    autoRefreshToken: true,
    detectSessionInUrl: true
  }
})

// Headers for calls to our own /api/* endpoints that require a logged-in
// staff session. The backend verifies the bearer token with Supabase and
// scopes the caller to the stores they own. Falls back to plain JSON
// headers when there is no session (the request then gets a 401).
export async function authHeaders() {
  let token = null
  try {
    const { data: { session } } = await supabase.auth.getSession()
    token = session?.access_token || null
  } catch (e) {
    console.error('authHeaders: could not read session', e)
  }
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  }
}

// Helper function to fetch receipt with all related data
export async function fetchReceiptById(receiptId) {
  try {
    // Every read here except the linked-units one below needs only the
    // receipt id, so they run together in one round trip instead of one
    // after another. Errors are checked in the same order as before.
    const [
      { data: receipt, error: receiptError },
      { data: items, error: itemsError },
      { data: itemStatuses, error: statusError },
      { data: preferences },
      { data: messages, error: messagesError },
      { data: reservedUnits }
    ] = await Promise.all([
      // Fetch main receipt
      supabase
        .from('receipts')
        .select('*')
        .eq('id', receiptId)
        .maybeSingle(),

      // Fetch receipt items - INCLUDE ID for inventory matching, set_id/set_name for grouped items
      supabase
        .from('receipt_items')
        .select('id, receipt_id, item_name, sku, color, quantity, price, position, set_id, set_name, manufacturer_id, manufacturer:manufacturers(id, name)')
        .eq('receipt_id', receiptId)
        .order('position'),

      // Fetch item statuses
      supabase
        .from('item_statuses')
        .select('*')
        .eq('receipt_id', receiptId),

      // Fetch customer preferences
      supabase
        .from('customer_preferences')
        .select('*')
        .eq('receipt_id', receiptId)
        .maybeSingle(),

      // Fetch messages (include ALL messages including survey for proper routing)
      supabase
        .from('messages')
        .select('*')
        .eq('receipt_id', receiptId)
        .order('created_at', { ascending: true }),

      // Fetch assigned inventory units for this receipt
      // FIX: Query by BOTH reserved_for_receipt_id AND receipt_item_id
      // This ensures delivered units (which might have null reserved_for_receipt_id) are still found
      // First, get units by reserved_for_receipt_id
      supabase
        .from('inventory_units')
        .select('*')
        .eq('reserved_for_receipt_id', receiptId)
    ])

    if (receiptError) throw receiptError
    if (!receipt) return null
    if (itemsError) throw itemsError
    if (statusError) throw statusError
    if (messagesError) throw messagesError

    const itemIds = (items || []).map(item => item.id).filter(Boolean)

    let inventoryUnits = reservedUnits || []
    
    // Also get units by receipt_item_id (for delivered units that may have null reserved_for_receipt_id)
    if (itemIds.length > 0) {
      const { data: linkedUnits } = await supabase
        .from('inventory_units')
        .select('*')
        .in('receipt_item_id', itemIds)
      
      // Merge, avoiding duplicates
      const existingIds = new Set(inventoryUnits.map(u => u.id))
      for (const unit of (linkedUnits || [])) {
        if (!existingIds.has(unit.id)) {
          inventoryUnits.push(unit)
        }
      }
    }

    // Filter out empty/invalid items for accurate counting
    const validItems = (items || []).filter(item => 
      (item.sku && item.sku.trim()) || (item.item_name && item.item_name.trim())
    )
    
    // Calculate inventory readiness
    const totalItems = validItems.length
    const assignedUnits = inventoryUnits?.length || 0
    const isReadyForDelivery = totalItems > 0 && assignedUnits >= totalItems

    // Combine all data
    return {
      ...receipt,
      items: validItems,  // Return only valid items
      itemStatuses: itemStatuses || [],
      customerPreferences: preferences || {},
      messages: messages || [],
      inventoryUnits: inventoryUnits || [],
      hasActiveChat: (messages && messages.length > 0) || receipt.has_active_chat,
      unreadMessages: receipt.unread_messages_count || 0,
      itemsReady: assignedUnits,
      isReadyForDelivery
    }
  } catch (error) {
    console.error('Error fetching receipt:', error)
    return null
  }
}

// ------------------------------------------------------------
// PUBLIC (no-login) receipt read for the customer-facing /receipt/:id page.
//
// Goes through the get_public_receipt(uuid) SECURITY DEFINER function
// (sql/anon-lockdown/01_create_public_receipt_rpc.sql) instead of reading
// tables directly, so the anon role needs NO table-level SELECT policy.
// The function returns a single receipt by UUID with an allow-list of
// customer-safe columns; it never lists receipts.
//
// Returns the same shape the page already consumed from fetchReceiptById
// (items, itemStatuses, ...) so nothing downstream changes. The dashboard
// keeps using fetchReceiptById under the logged-in owner policies.
// ------------------------------------------------------------
export async function fetchPublicReceipt(receiptId) {
  try {
    const { data, error } = await supabase.rpc('get_public_receipt', {
      p_receipt_id: receiptId
    })

    if (error) throw error
    if (!data) return null

    // Same "valid item" rule as fetchReceiptById so counts match the dashboard
    const validItems = (data.items || []).filter(item =>
      (item.sku && item.sku.trim()) || (item.item_name && item.item_name.trim())
    )

    return {
      ...data,
      items: validItems,
      itemStatuses: data.item_statuses || [],
      // Fields the page's shared helpers may touch; anon never had these.
      customerPreferences: {},
      messages: [],
      inventoryUnits: [],
      hasActiveChat: false,
      unreadMessages: 0,
      itemsReady: 0,
      isReadyForDelivery: false
    }
  } catch (error) {
    console.error('Error fetching public receipt:', error)
    return null
  }
}

// Helper function to fetch all receipts - SIMPLE VERSION (no joins)
// Now accepts optional storeId for multi-store filtering
export async function fetchAllReceipts(storeId = null) {
  try {
    console.log('🔄 Fetching all receipts...', storeId ? `for store ${storeId}` : '');
    
    // Build query
    let query = supabase
      .from('receipts')
      .select('*')
      .order('created_at', { ascending: false })

    // Filter by store if provided
    if (storeId) {
      query = query.eq('store_id', storeId)
    }

    const { data: receipts, error } = await query

    if (error) throw error

    console.log(`✅ Fetched ${receipts?.length || 0} receipts`);

    // Add basic info for display, LIST_BATCH_SIZE receipts per round of
    // queries instead of five queries per receipt. A batch that can't be
    // trusted is re-read the old per-receipt way, so the result is the same.
    const batches = []
    for (let i = 0; i < receipts.length; i += LIST_BATCH_SIZE) {
      batches.push(receipts.slice(i, i + LIST_BATCH_SIZE))
    }
    const built = await Promise.all(batches.map(async (batch) => {
      let rows = null
      try {
        rows = await fetchListInfoBatch(batch)
      } catch (e) {
        rows = null
      }
      if (!rows) {
        console.warn(`fetchAllReceipts: batch of ${batch.length} fell back to per-receipt queries`)
        rows = await Promise.all(batch.map(fetchListInfoSingle))
      }
      return rows
    }))
    const receiptsWithBasicInfo = built.flat()

    if (import.meta.env.DEV) verifyListLoaderParity(receipts, receiptsWithBasicInfo)

    return receiptsWithBasicInfo;
  } catch (error) {
    console.error('Error fetching receipts:', error);
    return [];
  }
}

const LIST_BATCH_SIZE = 50   // receipts per batched round of queries
const ID_CHUNK_SIZE = 100    // ids per .in() filter, keeps request URLs short

// Shapes one receipt for the dashboard list. Both loaders below go through
// this, so the batched and per-receipt paths return identical objects.
function buildListReceipt(receipt, items, messages, itemStatuses, reservedUnits, linkedUnits) {
  // Get assigned inventory units - by BOTH reserved_for_receipt_id AND receipt_item_id
  // This ensures delivered units (which might have null reserved_for_receipt_id) are still found
  const inventoryUnits = reservedUnits || []

  // Merge, avoiding duplicates
  const existingIds = new Set(inventoryUnits.map(u => u.id))
  for (const unit of (linkedUnits || [])) {
    if (!existingIds.has(unit.id)) {
      inventoryUnits.push(unit)
    }
  }

  // Filter out empty/invalid items for accurate counting
  const validItems = (items || []).filter(item =>
    (item.sku && item.sku.trim()) || (item.item_name && item.item_name.trim())
  )
  const totalItems = validItems.length

  // Only count units that are NOT with the customer as "ready"
  // (delivered AND picked_up are both terminal — statusConstants SSOT)
  const readyUnits = inventoryUnits?.filter(u => !UNIT_TERMINAL_STATUSES.includes(u.status)) || []
  const deliveredUnits = inventoryUnits?.filter(u => UNIT_TERMINAL_STATUSES.includes(u.status)) || []
  const assignedUnits = readyUnits.length

  return {
    ...receipt,
    items: validItems,  // Return only valid items
    messages: messages || [],
    itemStatuses: itemStatuses || [],
    inventoryUnits: inventoryUnits || [],
    hasActiveChat: messages && messages.length > 0,
    unreadMessages: receipt.unread_messages_count || 0,
    itemsReady: assignedUnits,
    itemsDelivered: deliveredUnits.length,
    // Only ready if we have items, all are assigned, and NONE are already delivered
    isReadyForDelivery: totalItems > 0 && assignedUnits >= totalItems && deliveredUnits.length === 0
  }
}

// The original per-receipt loader (five queries per receipt). Kept as the
// fallback for any batch that errors or may have been cut short.
async function fetchListInfoSingle(receipt) {
  // Get item count
  const { data: items } = await supabase
    .from('receipt_items')
    .select('id, sku, item_name')
    .eq('receipt_id', receipt.id)

  // Get message count
  const { data: messages } = await supabase
    .from('messages')
    .select('id')
    .eq('receipt_id', receipt.id)
    .neq('message_type', 'survey')

  // Get item statuses for inventory tracking
  const { data: itemStatuses } = await supabase
    .from('item_statuses')
    .select('*')
    .eq('receipt_id', receipt.id)

  const itemIds = (items || []).map(item => item.id).filter(Boolean)

  // First, get units by reserved_for_receipt_id
  const { data: reservedUnits } = await supabase
    .from('inventory_units')
    .select('id, unit_serial, sku, status, receipt_item_id')
    .eq('reserved_for_receipt_id', receipt.id)

  // Also get units by receipt_item_id (for delivered units that may have null reserved_for_receipt_id)
  let linkedUnits = []
  if (itemIds.length > 0) {
    const { data } = await supabase
      .from('inventory_units')
      .select('id, unit_serial, sku, status, receipt_item_id')
      .in('receipt_item_id', itemIds)
    linkedUnits = data
  }

  return buildListReceipt(receipt, items, messages, itemStatuses, reservedUnits, linkedUnits)
}

// One receipt in exactly the shape fetchAllReceipts gives the list, for
// updating a single card after a staff action without reloading them all.
export async function fetchListReceiptById(receiptId) {
  const { data: receipt, error } = await supabase
    .from('receipts')
    .select('*')
    .eq('id', receiptId)
    .maybeSingle()
  if (error || !receipt) return null
  return fetchListInfoSingle(receipt)
}

function chunkIds(ids, size) {
  const chunks = []
  for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size))
  return chunks
}

// One .in() read, split into ID_CHUNK_SIZE pieces. Returns null (untrusted)
// on any error, or when the exact count shows the API row cap cut rows off.
async function readRowsIn(table, columns, column, ids, refine = null) {
  const parts = await Promise.all(chunkIds(ids, ID_CHUNK_SIZE).map(async (chunk) => {
    let query = supabase.from(table).select(columns, { count: 'exact' }).in(column, chunk)
    if (refine) query = refine(query)
    const { data, error, count } = await query
    if (error || !data || count !== data.length) return null
    return data
  }))
  if (parts.some(p => p === null)) return null
  return parts.flat()
}

// Groups rows by a key column, dropping that column when the old
// per-receipt query didn't select it (so objects keep their old shape).
function groupRows(rows, keyOf, dropKey = null) {
  const groups = new Map()
  for (const row of rows) {
    const key = keyOf(row)
    let value = row
    if (dropKey) {
      value = { ...row }
      delete value[dropKey]
    }
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key).push(value)
  }
  return groups
}

// Batched loader: the same reads as fetchListInfoSingle for a whole batch
// of receipts at once. Returns null if any read is untrusted.
async function fetchListInfoBatch(batch) {
  const ids = batch.map(r => r.id)

  const [items, messages, itemStatuses, reservedUnits] = await Promise.all([
    readRowsIn('receipt_items', 'id, sku, item_name, receipt_id', 'receipt_id', ids),
    readRowsIn('messages', 'id, receipt_id', 'receipt_id', ids, q => q.neq('message_type', 'survey')),
    readRowsIn('item_statuses', '*', 'receipt_id', ids),
    readRowsIn('inventory_units', 'id, unit_serial, sku, status, receipt_item_id, reserved_for_receipt_id', 'reserved_for_receipt_id', ids)
  ])
  if (!items || !messages || !itemStatuses || !reservedUnits) return null

  const itemIds = items.map(item => item.id).filter(Boolean)
  let linkedUnits = []
  if (itemIds.length > 0) {
    linkedUnits = await readRowsIn('inventory_units', 'id, unit_serial, sku, status, receipt_item_id', 'receipt_item_id', itemIds)
    if (!linkedUnits) return null
  }

  const receiptOfItem = new Map(items.map(item => [item.id, item.receipt_id]))
  const itemsBy = groupRows(items, r => r.receipt_id, 'receipt_id')
  const messagesBy = groupRows(messages, r => r.receipt_id, 'receipt_id')
  const statusesBy = groupRows(itemStatuses, r => r.receipt_id)
  const reservedBy = groupRows(reservedUnits, r => r.reserved_for_receipt_id, 'reserved_for_receipt_id')
  const linkedBy = groupRows(linkedUnits, r => receiptOfItem.get(r.receipt_item_id))

  return batch.map(receipt => buildListReceipt(
    receipt,
    itemsBy.get(receipt.id) || [],
    messagesBy.get(receipt.id) || [],
    statusesBy.get(receipt.id) || [],
    reservedBy.get(receipt.id) || [],
    linkedBy.get(receipt.id) || []
  ))
}

// DEV ONLY: runs the re-fetches the receipt drawer used to do after
// fetchReceiptById (inventory x2, set fields, and for loadReceiptDetails the
// item_statuses columns) and logs whether the derived values match them.
// Callers guard with import.meta.env.DEV, so production never runs it.
export async function verifyDrawerFieldsParity(receiptId, receipt, { withStatuses }) {
  try {
    const itemIds = (receipt.items || []).map(item => item.id).filter(Boolean)
    const [{ data: reservedUnits }, linkedRes, setRes, statusRes] = await Promise.all([
      supabase.from('inventory_units').select('*').eq('reserved_for_receipt_id', receiptId),
      itemIds.length > 0
        ? supabase.from('inventory_units').select('*').in('receipt_item_id', itemIds)
        : Promise.resolve({ data: [] }),
      itemIds.length > 0
        ? supabase.from('receipt_items').select('id, set_id, set_name').in('id', itemIds)
        : Promise.resolve({ data: [] }),
      withStatuses
        ? supabase.from('item_statuses').select('receipt_item_id, item_name, sku, status, notes, updated_at').eq('receipt_id', receiptId)
        : Promise.resolve({ data: null })
    ])

    // Old inventory merge (Map: reserved first, linked overwrite/append)
    const unitMap = new Map()
    ;(reservedUnits || []).forEach(u => unitMap.set(u.id, u))
    ;(linkedRes.data || []).forEach(u => unitMap.set(u.id, u))
    const legacyUnits = Array.from(unitMap.values())

    // Old set-field merge
    const setFieldsMap = {}
    ;(setRes.data || []).forEach(row => {
      if (row.set_id) setFieldsMap[row.id] = { set_id: row.set_id, set_name: row.set_name }
    })
    const legacyItems = (receipt.items || []).map(item => ({
      ...item,
      set_id: setFieldsMap[item.id]?.set_id || item.set_id || null,
      set_name: setFieldsMap[item.id]?.set_name || item.set_name || null
    }))

    // Unordered queries can return the same rows in a different order from
    // one call to the next; report that separately from a real difference.
    const sortKey = (row) => JSON.stringify(row)
    const sorted = (rows) => JSON.stringify([...(rows || [])].map(sortKey).sort())
    const problems = []
    const orderOnly = []
    const compare = (name, legacy, derived) => {
      if (JSON.stringify(legacy) === JSON.stringify(derived)) return
      if (sorted(legacy) === sorted(derived)) orderOnly.push(name)
      else problems.push(name)
    }
    compare('inventoryUnits', legacyUnits, receipt.inventoryUnits)
    compare('items', legacyItems, receipt.items)
    if (withStatuses) compare('itemStatuses', statusRes.data || [], receipt.itemStatuses)

    if (problems.length === 0) {
      console.log(`✅ DRAWER PARITY OK for receipt ${receiptId}` + (withStatuses ? ' (incl. item statuses)' : '') +
        (orderOnly.length ? ` (row order differs in: ${orderOnly.join(', ')})` : ''))
    } else {
      console.error(`❌ DRAWER PARITY MISMATCH for receipt ${receiptId}: ${problems.join(', ')}`, {
        legacyUnits, derivedUnits: receipt.inventoryUnits,
        legacyItems, derivedItems: receipt.items,
        legacyStatuses: statusRes.data, derivedStatuses: receipt.itemStatuses
      })
    }
  } catch (e) {
    console.error('DRAWER PARITY check failed to run', e)
  }
}

// DEV ONLY: once per page load, re-reads the list the old per-receipt way
// and logs whether the batched result matches it. Never runs in production.
let listParityChecked = false
async function verifyListLoaderParity(receipts, batched) {
  if (listParityChecked || receipts.length === 0) return
  listParityChecked = true
  const byId = (a, b) => String(a.id).localeCompare(String(b.id))
  const normalize = (r) => JSON.stringify({
    ...r,
    items: [...r.items].sort(byId),
    messages: [...r.messages].sort(byId),
    itemStatuses: [...r.itemStatuses].sort(byId),
    inventoryUnits: [...r.inventoryUnits].sort(byId)
  })
  const legacy = await Promise.all(receipts.map(fetchListInfoSingle))
  const mismatches = []
  const orderOnlyDetails = []
  legacy.forEach((old, i) => {
    if (JSON.stringify(old) === JSON.stringify(batched[i])) return
    if (normalize(old) === normalize(batched[i])) {
      const fields = ['items', 'messages', 'itemStatuses', 'inventoryUnits']
        .filter(f => JSON.stringify(old[f]) !== JSON.stringify(batched[i][f]))
      orderOnlyDetails.push(`${old.id}: ${fields.join(', ')}`)
      return
    }
    mismatches.push({ id: old.id, legacy: old, batched: batched[i] })
  })
  const orderOnly = orderOnlyDetails.length
  if (orderOnly) console.log('PARITY order-only differences (receipt: arrays):\n' + orderOnlyDetails.join('\n'))
  if (mismatches.length === 0) {
    console.log(`✅ PARITY OK: batched receipt list matches per-receipt list for all ${legacy.length} receipts` +
      (orderOnly ? ` (${orderOnly} differ only in row order)` : ''))
  } else {
    console.error(`❌ PARITY MISMATCH on ${mismatches.length} of ${legacy.length} receipts`, mismatches.slice(0, 5))
  }
}

// Helper function to create receipt
// Now accepts optional storeId and checks for existing inventory
export async function createReceipt(receiptData, storeId = null) {
  try {
    // Insert main receipt
    const { data: receipt, error: receiptError } = await supabase
      .from('receipts')
      .insert([{
        store_id: storeId || null,
        customer_name: receiptData.customerName,
        customer_phone: receiptData.customerPhone,
        customer_email: receiptData.customerEmail,
        business_name: receiptData.businessName || 'Furniture Store',
        business_phone: receiptData.businessPhone || '',
        business_address: receiptData.businessAddress,
        subtotal: receiptData.subtotal,
        tax: receiptData.tax,
        total: receiptData.total,
        delivery_status: receiptData.deliveryStatus || 'pending',
        delivery_date: receiptData.deliveryDate || null,
        estimated_time_slot: receiptData.estimatedTimeSlot || null,
        customer_notes: receiptData.notes || null,
        internal_notes: receiptData.internalNotes || null,
        is_ai_handling: true,
        sent_at: new Date().toISOString()
      }])
      .select()
      .single()

    if (receiptError) throw receiptError

    const receiptId = receipt.id

    // Insert receipt items
    if (receiptData.items && receiptData.items.length > 0) {
      const itemsToInsert = receiptData.items.map((item, index) => ({
        receipt_id: receiptId,
        item_name: item.name,
        sku: item.sku,
        color: item.color,
        quantity: item.quantity,
        price: item.price,
        position: index,
        manufacturer_id: item.manufacturerId || null
      }))

      const { error: itemsError } = await supabase
        .from('receipt_items')
        .insert(itemsToInsert)

      if (itemsError) throw itemsError

      // Check for existing available inventory and auto-assign
      if (storeId) {
        await checkAndAssignExistingInventory(receiptId, receiptData.items, storeId)
      }
    }

    // Insert item statuses
    if (receiptData.itemStatuses && receiptData.itemStatuses.length > 0) {
      const statusesToInsert = receiptData.itemStatuses.map(status => ({
        receipt_id: receiptId,
        item_name: status.itemName,
        sku: status.sku,
        status: status.status,
        notes: status.notes
      }))

      const { error: statusError } = await supabase
        .from('item_statuses')
        .insert(statusesToInsert)

      if (statusError) throw statusError
    }

    // Insert customer preferences
    if (receiptData.customerPreferences) {
      const { error: prefsError } = await supabase
        .from('customer_preferences')
        .insert([{
          receipt_id: receiptId,
          preferred_delivery_day: receiptData.customerPreferences.preferredDeliveryDay,
          preferred_time_window: receiptData.customerPreferences.preferredTimeWindow,
          notes: receiptData.customerPreferences.notes
        }])

      if (prefsError) throw prefsError
    }

    return receiptId
  } catch (error) {
    console.error('Error creating receipt:', error)
    throw error
  }
}

// Helper function to update receipt
export async function updateReceipt(receiptId, updates) {
  try {
    const { error } = await supabase
      .from('receipts')
      .update({
        customer_name: updates.customerName,
        customer_phone: updates.customerPhone,
        delivery_status: updates.deliveryStatus,
        delivery_date: updates.deliveryDate,
        estimated_time_slot: updates.estimatedTimeSlot,
        customer_notes: updates.notes,
        internal_notes: updates.internalNotes
      })
      .eq('id', receiptId)

    if (error) throw error

    return true
  } catch (error) {
    console.error('Error updating receipt:', error)
    throw error
  }
}

// Helper to send message
export async function sendMessage(receiptId, role, content, sentVia = 'web') {
  try {
    const { data, error } = await supabase
      .from('messages')
      .insert([{
        receipt_id: receiptId,
        role: role,
        content: content,
        sent_via: sentVia,
        is_read: role !== 'user'
      }])
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error sending message:', error)
    throw error
  }
}

// Helper to mark messages as read
export async function markMessagesAsRead(receiptId) {
  try {
    const { error } = await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('receipt_id', receiptId)
      .eq('is_read', false)

    if (error) throw error
  } catch (error) {
    console.error('Error marking messages as read:', error)
  }
}

// Helper to delete receipt and all related data
export async function deleteReceipt(receiptId) {
  try {
    console.log('🗑️ Starting receipt deletion for:', receiptId);
    
    // Delete all related data first (due to foreign key constraints)
    
    // Release any reserved inventory units first
    const { error: invError } = await supabase
      .from('inventory_units')
      .update({ 
        status: 'available', 
        reserved_for_receipt_id: null,
        reserved_at: null,
        reserved_by: null
      })
      .eq('reserved_for_receipt_id', receiptId)
    
    if (invError) console.warn('inventory_units update warning:', invError);
    else console.log('✓ Released inventory units');

    // Delete messages
    const { error: messagesError } = await supabase
      .from('messages')
      .delete()
      .eq('receipt_id', receiptId)
    
    if (messagesError) {
      console.error('❌ messages delete error:', messagesError);
      throw messagesError;
    }
    console.log('✓ Deleted messages');

    // Delete item statuses
    const { error: statusError } = await supabase
      .from('item_statuses')
      .delete()
      .eq('receipt_id', receiptId)
    
    if (statusError) {
      console.error('❌ item_statuses delete error:', statusError);
      throw statusError;
    }
    console.log('✓ Deleted item_statuses');

    // Delete customer preferences
    const { error: prefsError } = await supabase
      .from('customer_preferences')
      .delete()
      .eq('receipt_id', receiptId)
    
    if (prefsError) {
      console.error('❌ customer_preferences delete error:', prefsError);
      throw prefsError;
    }
    console.log('✓ Deleted customer_preferences');

    // Delete receipt items
    const { error: itemsError } = await supabase
      .from('receipt_items')
      .delete()
      .eq('receipt_id', receiptId)
    
    if (itemsError) {
      console.error('❌ receipt_items delete error:', itemsError);
      throw itemsError;
    }
    console.log('✓ Deleted receipt_items');

    // Delete notifications (if they exist)
    const { error: notifError } = await supabase
      .from('notifications')
      .delete()
      .eq('receipt_id', receiptId)
    
    if (notifError) console.warn('notifications delete warning:', notifError);
    else console.log('✓ Deleted notifications');

    // Delete scheduled messages (if they exist)
    const { error: schedError } = await supabase
      .from('scheduled_messages')
      .delete()
      .eq('receipt_id', receiptId)
    
    if (schedError) console.warn('scheduled_messages delete warning:', schedError);
    else console.log('✓ Deleted scheduled_messages');

    // Delete customer feedback (if it exists)
    const { error: feedbackError } = await supabase
      .from('customer_feedback')
      .delete()
      .eq('receipt_id', receiptId)
    
    if (feedbackError) console.warn('customer_feedback delete warning:', feedbackError);
    else console.log('✓ Deleted customer_feedback');

    // Delete delivery route stops (if they exist)
    const { error: stopsError } = await supabase
      .from('delivery_route_stops')
      .delete()
      .eq('receipt_id', receiptId)
    
    if (stopsError) console.warn('delivery_route_stops delete warning:', stopsError);
    else console.log('✓ Deleted delivery_route_stops');

    // Finally, delete the main receipt
    console.log('🗑️ Deleting main receipt...');
    const { error: receiptError } = await supabase
      .from('receipts')
      .delete()
      .eq('id', receiptId)
    
    if (receiptError) {
      console.error('❌ receipts delete error:', receiptError);
      throw receiptError;
    }
    
    console.log('✅ Receipt deleted successfully');
    return true
  } catch (error) {
    console.error('Error deleting receipt:', error)
    throw error
  }
}

// ========================================
// STORE FUNCTIONS
// ========================================

// Fetch all stores
export async function fetchAllStores() {
  try {
    const { data, error } = await supabase
      .from('stores')
      .select('*')
      .order('name')

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching stores:', error)
    return []
  }
}

// Fetch store by slug
export async function fetchStoreBySlug(slug) {
  try {
    const { data, error } = await supabase
      .from('stores')
      .select('*')
      .eq('slug', slug)
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error fetching store by slug:', error)
    return null
  }
}

// Fetch store by ID
export async function fetchStoreById(storeId) {
  try {
    const { data, error } = await supabase
      .from('stores')
      .select('*')
      .eq('id', storeId)
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error fetching store by ID:', error)
    return null
  }
}

// ========================================
// MANUFACTURER FUNCTIONS
// ========================================

// Fetch all manufacturers for a store
export async function fetchManufacturers(storeId) {
  try {
    if (!storeId) return []
    
    const { data, error } = await supabase
      .from('manufacturers')
      .select('id, name, created_at')
      .eq('store_id', storeId)
      .order('name')

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching manufacturers:', error)
    return []
  }
}

// Create a new manufacturer for a store (handles duplicates gracefully)
export async function createManufacturer(storeId, name) {
  try {
    if (!storeId || !name?.trim()) return null

    const trimmedName = name.trim()

    // Check if it already exists (case-insensitive)
    const { data: existing } = await supabase
      .from('manufacturers')
      .select('id, name')
      .eq('store_id', storeId)
      .ilike('name', trimmedName)
      .maybeSingle()

    if (existing) return existing

    // Create new
    const { data, error } = await supabase
      .from('manufacturers')
      .insert({ store_id: storeId, name: trimmedName })
      .select('id, name')
      .single()

    if (error) {
      // Race condition: another request created it between our check and insert
      if (error.code === '23505') {
        const { data: retry } = await supabase
          .from('manufacturers')
          .select('id, name')
          .eq('store_id', storeId)
          .ilike('name', trimmedName)
          .maybeSingle()
        return retry
      }
      throw error
    }

    return data
  } catch (error) {
    console.error('Error creating manufacturer:', error)
    return null
  }
}

// ========================================
// INVENTORY FUNCTIONS
// ========================================

// Get store inventory summary (available/reserved counts by SKU)
export async function getStoreInventorySummary(storeId) {
  try {
    const { data, error } = await supabase
      .from('inventory_units')
      .select('sku, description, status')
      .eq('store_id', storeId)
      .in('status', ['available', 'reserved'])

    if (error) throw error

    // Group by SKU and count
    const summary = {}
    for (const unit of data || []) {
      if (!summary[unit.sku]) {
        summary[unit.sku] = {
          sku: unit.sku,
          description: unit.description,
          available: 0,
          reserved: 0,
          total: 0
        }
      }
      if (unit.status === 'available') {
        summary[unit.sku].available++
      } else if (unit.status === 'reserved') {
        summary[unit.sku].reserved++
      }
      summary[unit.sku].total++
    }

    return Object.values(summary)
  } catch (error) {
    console.error('Error fetching inventory summary:', error)
    return []
  }
}

// Fetch inventory units for a store with optional filters
export async function fetchStoreInventoryUnits(storeId, filters = {}) {
  try {
    let query = supabase
      .from('inventory_units')
      .select('*')
      .eq('store_id', storeId)
      .order('arrived_at', { ascending: false })

    if (filters.status) {
      query = query.eq('status', filters.status)
    }
    if (filters.sku) {
      query = query.ilike('sku', `%${filters.sku}%`)
    }
    if (filters.location) {
      query = query.eq('location', filters.location)
    }

    const { data, error } = await query

    if (error) throw error
    return data || []
  } catch (error) {
    console.error('Error fetching inventory units:', error)
    return []
  }
}

// Check for existing available inventory and assign to new receipt
export async function checkAndAssignExistingInventory(receiptId, items, storeId) {
  try {
    console.log('🔍 Checking for existing inventory to assign...')
    console.log('🏪 Store ID:', storeId)
    let assignedCount = 0

    // FIX: First, get the receipt_items with their IDs so we can match to specific items
    const { data: receiptItems, error: itemsError } = await supabase
      .from('receipt_items')
      .select('id, sku, item_name, position')
      .eq('receipt_id', receiptId)
      .order('position')
    
    if (itemsError) {
      console.error('Error fetching receipt items:', itemsError)
      return 0
    }

    console.log(`📋 Found ${receiptItems?.length || 0} receipt items to check`)

    // Process each receipt_item individually (each exploded item needs its own unit)
    for (const receiptItem of (receiptItems || [])) {
      if (!receiptItem.sku) continue

      // Check if this specific item already has a unit assigned
      const { data: existingUnit } = await supabase
        .from('inventory_units')
        .select('id')
        .eq('receipt_item_id', receiptItem.id)
        .maybeSingle()
      
      if (existingUnit) {
        console.log(`📦 Item ${receiptItem.id} (${receiptItem.sku}) already has unit assigned`)
        continue
      }

      let availableUnit = null

      // Try to find a unit for this store first
      if (storeId) {
        const { data: storeUnit } = await supabase
          .from('inventory_units')
          .select('*')
          .eq('store_id', storeId)
          .ilike('sku', receiptItem.sku)
          .eq('status', 'available')
          .is('reserved_for_receipt_id', null)
          .is('receipt_item_id', null)  // Not assigned to another item
          .order('arrived_at', { ascending: true }) // FIFO
          .limit(1)
          .maybeSingle()

        if (storeUnit) {
          availableUnit = storeUnit
          console.log(`📦 Found store unit for ${receiptItem.sku}`)
        }
      }

      // If no store-specific unit, try without store filter
      if (!availableUnit) {
        const { data: anyUnit } = await supabase
          .from('inventory_units')
          .select('*')
          .ilike('sku', receiptItem.sku)
          .eq('status', 'available')
          .is('reserved_for_receipt_id', null)
          .is('receipt_item_id', null)  // Not assigned to another item
          .order('arrived_at', { ascending: true }) // FIFO
          .limit(1)
          .maybeSingle()

        if (anyUnit) {
          availableUnit = anyUnit
        }
      }

      if (!availableUnit) {
        console.log(`⏳ No available units for item ${receiptItem.id} (${receiptItem.sku})`)
        continue
      }

      // Assign this unit to this SPECIFIC receipt_item
      const { error: assignError } = await supabase
        .from('inventory_units')
        .update({
          status: 'reserved',
          reserved_for_receipt_id: receiptId,
          receipt_item_id: receiptItem.id,  // ← THE KEY FIX!
          reserved_at: new Date().toISOString()
        })
        .eq('id', availableUnit.id)

      if (!assignError) {
        assignedCount++
        console.log(`✅ Assigned unit ${availableUnit.unit_serial} to item ${receiptItem.id} (${receiptItem.sku})`)
      } else {
        console.error(`Error assigning unit:`, assignError)
      }
    }

    console.log(`📦 Auto-assigned ${assignedCount} units to specific receipt items`)
    return assignedCount
  } catch (error) {
    console.error('Error assigning existing inventory:', error)
    return 0
  }
}

// Mark all units for a receipt as delivered
export async function deliverReceiptUnits(receiptId) {
  try {
    // First get the units to know which items we're delivering
    const { data: unitsToDeliver } = await supabase
      .from('inventory_units')
      .select('id, sku, receipt_item_id')
      .eq('reserved_for_receipt_id', receiptId)
      .eq('status', 'reserved')
    
    // Mark inventory units as delivered
    const { data, error } = await supabase
      .from('inventory_units')
      .update({
        status: 'delivered',
        location: 'customer',
        delivered_at: new Date().toISOString()
      })
      .eq('reserved_for_receipt_id', receiptId)
      .eq('status', 'reserved')
      .select()

    if (error) throw error
    console.log(`✅ Marked ${data?.length || 0} units as delivered`)
    
    // Also update item_statuses to 'delivered' for consistency
    if (unitsToDeliver && unitsToDeliver.length > 0) {
      for (const unit of unitsToDeliver) {
        if (unit.receipt_item_id) {
          await supabase
            .from('item_statuses')
            .upsert({
              receipt_id: receiptId,
              receipt_item_id: unit.receipt_item_id,
              sku: unit.sku,
              status: 'delivered',
              notes: `Delivered on ${new Date().toLocaleDateString()}`,
              updated_at: new Date().toISOString()
            }, { onConflict: 'receipt_id,receipt_item_id' })
        } else {
          // Legacy fallback for units without receipt_item_id
          await supabase
            .from('item_statuses')
            .update({
              status: 'delivered',
              notes: `Delivered on ${new Date().toLocaleDateString()}`,
              updated_at: new Date().toISOString()
            })
            .eq('receipt_id', receiptId)
            .ilike('sku', unit.sku)
        }
      }
      console.log(`✅ Updated item_statuses for ${unitsToDeliver.length} units to delivered`)
    }
    
    return data || []
  } catch (error) {
    console.error('Error delivering receipt units:', error)
    return []
  }
}

// =====================================================
// Bulk-mark every item on a receipt as delivered / picked_up in
// item_statuses. This replaces deliverReceiptUnits in the "order marked
// delivered from the edit modal" flow now that inventory is retired:
// item_statuses is the SOLE fulfillment source (chat.py, incoming-webhook,
// dashboard readiness, digital receipt all read it), so without this sweep
// a receipt can say 'delivered' while the AI tells the customer their
// items are still ready/on order.
// - Keyed on (receipt_id, receipt_item_id) — works for no-item-number
//   (MISC-) items exactly like everything else.
// - Items already terminal keep their status (a picked_up item is not
//   rewritten to delivered, and vice versa).
// =====================================================
export async function markAllItemStatusesDelivered(receiptId, finalStatus = 'delivered') {
  try {
    // Normalize: only terminal statuses are valid here
    const status = ITEM_TERMINAL_STATUSES.includes(finalStatus) ? finalStatus : 'delivered'

    const { data: items, error: itemsError } = await supabase
      .from('receipt_items')
      .select('id, sku, item_name')
      .eq('receipt_id', receiptId)

    if (itemsError) throw itemsError
    if (!items || items.length === 0) return { updated: 0 }

    // Don't overwrite rows that are already terminal
    const { data: existing } = await supabase
      .from('item_statuses')
      .select('receipt_item_id, status')
      .eq('receipt_id', receiptId)

    const alreadyTerminal = new Set(
      (existing || [])
        .filter(s => s.receipt_item_id && ITEM_TERMINAL_STATUSES.includes(s.status))
        .map(s => String(s.receipt_item_id))
    )

    const now = new Date().toISOString()
    const rows = items
      .filter(item => !alreadyTerminal.has(String(item.id)))
      .map(item => ({
        receipt_id: receiptId,
        receipt_item_id: item.id,
        item_name: item.item_name || item.sku || 'Item', // item_statuses.item_name is NOT NULL
        sku: item.sku || null,
        status: status,
        notes: `Order marked ${status === 'picked_up' ? 'picked up' : 'delivered'} ${new Date().toLocaleDateString()}`,
        updated_at: now
      }))

    if (rows.length === 0) {
      console.log('✅ markAllItemStatusesDelivered: all items already terminal')
      return { updated: 0 }
    }

    const { error: upsertError } = await supabase
      .from('item_statuses')
      .upsert(rows, { onConflict: 'receipt_id,receipt_item_id' })

    if (upsertError) throw upsertError
    console.log(`✅ markAllItemStatusesDelivered: ${rows.length} item(s) → ${status}`)
    return { updated: rows.length }
  } catch (error) {
    console.error('Error bulk-marking item statuses:', error)
    return { updated: 0, error: error.message }
  }
}

// Mark specific items as delivered (for partial delivery)
export async function deliverPartialItems(receiptId, skusToDeliver) {
  try {
    const results = {
      unitsDelivered: [],
      itemStatusesUpdated: []
    }

    // Update inventory units for the specific SKUs
    for (const sku of skusToDeliver) {
      const { data: units, error: unitError } = await supabase
        .from('inventory_units')
        .update({
          status: 'delivered',
          location: 'customer',
          delivered_at: new Date().toISOString()
        })
        .eq('reserved_for_receipt_id', receiptId)
        .ilike('sku', sku)
        .in('status', ['reserved', 'out_for_delivery'])
        .select()

      if (!unitError && units) {
        results.unitsDelivered.push(...units)
      }

      // Also update item_statuses table for UI consistency
      // Use receipt_item_id from the delivered units when available
      if (!units || units.length === 0) {
        // NO-STOCK PATH: no inventory units for this SKU — item_statuses is
        // the source of truth. Mark every matching line item delivered.
        const { data: lineRows, error: lineErr } = await supabase
          .from('receipt_items')
          .select('id, sku, item_name')
          .eq('receipt_id', receiptId)
          .ilike('sku', sku)

        if (lineErr) {
          console.error('Error fetching receipt items for no-stock delivery:', lineErr)
        }

        for (const row of (lineRows || [])) {
          const { data: statusUpdate, error: statusError } = await supabase
            .from('item_statuses')
            .upsert({
              receipt_id: receiptId,
              receipt_item_id: row.id,
              sku: row.sku || sku,
              item_name: row.item_name || row.sku || 'Item',
              status: 'delivered',
              notes: `Delivered on ${new Date().toLocaleDateString()}`,
              updated_at: new Date().toISOString()
            }, { onConflict: 'receipt_id,receipt_item_id' })
            .select()

          if (!statusError && statusUpdate) {
            results.itemStatusesUpdated.push(...statusUpdate)
          }
        }
      }
      if (units && units.length > 0) {
        for (const unit of units) {
          if (unit.receipt_item_id) {
            const { data: statusUpdate, error: statusError } = await supabase
              .from('item_statuses')
              .upsert({
                receipt_id: receiptId,
                receipt_item_id: unit.receipt_item_id,
                sku: unit.sku || sku,
                status: 'delivered',
                notes: `Delivered on ${new Date().toLocaleDateString()}`,
                updated_at: new Date().toISOString()
              }, { onConflict: 'receipt_id,receipt_item_id' })
              .select()

            if (!statusError && statusUpdate) {
              results.itemStatusesUpdated.push(...statusUpdate)
            }
          } else {
            // Legacy fallback
            const { data: statusUpdate, error: statusError } = await supabase
              .from('item_statuses')
              .update({
                status: 'delivered',
                notes: `Delivered on ${new Date().toLocaleDateString()}`,
                updated_at: new Date().toISOString()
              })
              .eq('receipt_id', receiptId)
              .ilike('sku', sku)
              .select()

            if (!statusError && statusUpdate) {
              results.itemStatusesUpdated.push(...statusUpdate)
            }
          }
        }
      }
    }

    console.log(`✅ Partial delivery: ${results.unitsDelivered.length} units, ${results.itemStatusesUpdated.length} statuses updated`)
    return results
  } catch (error) {
    console.error('Error delivering partial items:', error)
    return { unitsDelivered: [], itemStatusesUpdated: [] }
  }
}

// Check if all items on a receipt are delivered
// Uses inventory_units as source of truth (serialization system)

// Check if all items on a receipt are delivered
// FIXED: Now checks both item_statuses AND inventory_units, uses higher count
export async function checkAllItemsDelivered(receiptId) {
  try {
    // =====================================================
    // 1. Get all receipt items with quantities (source of truth)
    // =====================================================
    const { data: items, error: itemsError } = await supabase
      .from('receipt_items')
      .select('sku, quantity, item_name')
      .eq('receipt_id', receiptId)

    if (itemsError) throw itemsError
    
    // Filter out empty/invalid items (items with no SKU AND no item_name)
    const validItems = (items || []).filter(item => 
      (item.sku && item.sku.trim()) || (item.item_name && item.item_name.trim())
    )
    
    if (validItems.length === 0) {
      return { allDelivered: true, totalItems: 0, deliveredCount: 0, remainingCount: 0 }
    }

    // Build SKU -> quantity map (needed for item_statuses counting)
    const skuQuantities = {}
    let totalNeeded = 0
    
    for (const item of validItems) {
      const qty = item.quantity || 1
      totalNeeded += qty
      const skuKey = (item.sku || '').toLowerCase().trim()
      if (skuKey) {
        skuQuantities[skuKey] = (skuQuantities[skuKey] || 0) + qty
      }
    }
    
    console.log(`📊 Total items needed: ${totalNeeded} (from ${validItems.length} line items)`)

    // =====================================================
    // 2. CHECK ITEM_STATUSES FIRST (most reliable for UI sync)
    // This is the KEY FIX - always check this, not just as fallback
    // =====================================================
    const { data: itemStatuses, error: statusError } = await supabase
      .from('item_statuses')
      .select('sku, status')
      .eq('receipt_id', receiptId)
    
    let statusesDelivered = 0
    const deliveredSkus = new Set()
    
    if (!statusError && itemStatuses) {
      for (const status of itemStatuses) {
        if (ITEM_TERMINAL_STATUSES.includes(status.status)) {
          const skuKey = (status.sku || '').toLowerCase().trim()
          if (skuKey) {
            deliveredSkus.add(skuKey)
          }
        }
      }
      
      // KEY FIX: Map delivered SKUs back to their quantities from receipt_items
      // This ensures we count "2 chairs delivered" not "1 SKU delivered"
      for (const sku of deliveredSkus) {
        statusesDelivered += skuQuantities[sku] || 1
      }
    }
    
    console.log(`📊 From item_statuses: ${statusesDelivered}/${totalNeeded} delivered (SKUs: ${[...deliveredSkus].join(', ')})`)

    // =====================================================
    // 3. ALSO CHECK INVENTORY_UNITS (for inventory-tracked receipts)
    // =====================================================
    let inventoryDelivered = 0
    
    const { data: units, error: unitsError } = await supabase
      .from('inventory_units')
      .select('sku, status')
      .eq('reserved_for_receipt_id', receiptId)

    if (!unitsError && units && units.length > 0) {
      inventoryDelivered = units.filter(u => UNIT_TERMINAL_STATUSES.includes(u.status)).length
      console.log(`📊 From inventory_units: ${inventoryDelivered}/${units.length} units delivered`)
    } else {
      console.log(`📊 No inventory_units found for this receipt`)
    }

    // =====================================================
    // 4. USE THE HIGHER COUNT (handles edge cases where one 
    //    source is more up-to-date than the other)
    // =====================================================
    const deliveredCount = Math.max(statusesDelivered, inventoryDelivered)
    console.log(`📊 Using delivered count: ${deliveredCount} (statuses=${statusesDelivered}, inventory=${inventoryDelivered})`)

    // Check if all items delivered
    const allDelivered = totalNeeded > 0 && deliveredCount >= totalNeeded

    console.log(`📊 checkAllItemsDelivered: allDelivered=${allDelivered}`)

    return {
      allDelivered,
      totalItems: totalNeeded,
      deliveredCount,
      remainingCount: Math.max(0, totalNeeded - deliveredCount),
      skuQuantities
    }
  } catch (error) {
    console.error('Error checking delivery status:', error)
    return { allDelivered: false, totalItems: 0, deliveredCount: 0, remainingCount: 0 }
  }
}

// Cancel/release units back to available when order is cancelled
export async function cancelReceiptUnits(receiptId) {
  try {
    const { data, error } = await supabase
      .from('inventory_units')
      .update({
        status: 'available',
        reserved_for_receipt_id: null,
        reserved_at: null,
        reserved_by: null
      })
      .eq('reserved_for_receipt_id', receiptId)
      .in('status', ['reserved', 'out_for_delivery'])
      .select()

    if (error) throw error
    console.log(`✅ Released ${data?.length || 0} units back to available`)
    
    // After releasing, try to auto-assign to waiting orders
    // The DB trigger should handle this, but we can also manually trigger
    return data || []
  } catch (error) {
    console.error('Error cancelling receipt units:', error)
    return []
  }
}

// Manually assign a specific unit to a receipt
export async function assignUnitToReceipt(unitId, receiptId, assignedBy = 'staff') {
  try {
    const { data, error } = await supabase
      .from('inventory_units')
      .update({
        status: 'reserved',
        reserved_for_receipt_id: receiptId,
        reserved_at: new Date().toISOString(),
        reserved_by: assignedBy
      })
      .eq('id', unitId)
      .eq('status', 'available')
      .select()
      .single()

    if (error) throw error
    return data
  } catch (error) {
    console.error('Error assigning unit to receipt:', error)
    return null
  }
}

// Get waiting orders for a specific SKU at a store
export async function getWaitingOrdersForSku(storeId, sku) {
  try {
    // Get receipts that have this SKU but don't have enough units assigned
    const { data: receipts, error } = await supabase
      .from('receipts')
      .select(`
        id,
        customer_name,
        customer_phone,
        created_at,
        delivery_status
      `)
      .eq('store_id', storeId)
      .in('delivery_status', ['pending', 'processing'])
      .order('created_at', { ascending: true })

    if (error) throw error

    // Filter to only those needing this SKU
    const waitingOrders = []
    for (const receipt of receipts || []) {
      // Get items for this receipt
      const { data: items } = await supabase
        .from('receipt_items')
        .select('sku, quantity')
        .eq('receipt_id', receipt.id)
        .ilike('sku', sku)

      if (!items || items.length === 0) continue

      // Get assigned units for this receipt and SKU
      const { data: assignedUnits } = await supabase
        .from('inventory_units')
        .select('id')
        .eq('reserved_for_receipt_id', receipt.id)
        .ilike('sku', sku)

      const neededQty = items.reduce((sum, i) => sum + (i.quantity || 1), 0)
      const assignedQty = assignedUnits?.length || 0

      if (assignedQty < neededQty) {
        waitingOrders.push({
          ...receipt,
          sku,
          neededQty,
          assignedQty,
          stillNeeds: neededQty - assignedQty
        })
      }
    }

    return waitingOrders
  } catch (error) {
    console.error('Error getting waiting orders:', error)
    return []
  }
}

// Get all waiting orders for a store (queue view)
export async function getWaitingOrders(storeId) {
  try {
    // Get all pending/processing receipts for this store
    const { data: receipts, error } = await supabase
      .from('receipts')
      .select('id, customer_name, customer_phone, created_at, delivery_status')
      .eq('store_id', storeId)
      .in('delivery_status', ['pending', 'processing'])
      .order('created_at', { ascending: true })

    if (error) throw error

    const waitingOrders = []

    for (const receipt of receipts || []) {
      // Get all items for this receipt
      const { data: items } = await supabase
        .from('receipt_items')
        .select('sku, item_name, quantity')
        .eq('receipt_id', receipt.id)

      // Get all assigned units
      const { data: assignedUnits } = await supabase
        .from('inventory_units')
        .select('sku')
        .eq('reserved_for_receipt_id', receipt.id)

      // Build item status
      const itemStatus = []
      for (const item of items || []) {
        const needed = item.quantity || 1
        const assigned = (assignedUnits || []).filter(
          u => u.sku.toLowerCase() === item.sku.toLowerCase()
        ).length

        if (assigned < needed) {
          itemStatus.push({
            sku: item.sku,
            itemName: item.item_name,
            needed,
            assigned,
            stillNeeds: needed - assigned
          })
        }
      }

      if (itemStatus.length > 0) {
        waitingOrders.push({
          ...receipt,
          waitingItems: itemStatus,
          totalWaiting: itemStatus.reduce((sum, i) => sum + i.stillNeeds, 0)
        })
      }
    }

    return waitingOrders
  } catch (error) {
    console.error('Error getting waiting orders:', error)
    return []
  }
}

// Get receipt inventory status (which units are assigned)
export async function getReceiptInventoryStatus(receiptId) {
  try {
    // Get receipt items
    const { data: items, error: itemsError } = await supabase
      .from('receipt_items')
      .select('sku, item_name, quantity')
      .eq('receipt_id', receiptId)

    if (itemsError) throw itemsError

    // Get assigned units
    const { data: units, error: unitsError } = await supabase
      .from('inventory_units')
      .select('*')
      .eq('reserved_for_receipt_id', receiptId)

    if (unitsError) throw unitsError

    // Build status for each item
    const itemsWithUnits = (items || []).map(item => {
      const assignedUnits = (units || []).filter(
        u => u.sku.toLowerCase() === item.sku.toLowerCase()
      )
      return {
        ...item,
        assignedUnits,
        needed: item.quantity || 1,
        assigned: assignedUnits.length,
        isComplete: assignedUnits.length >= (item.quantity || 1)
      }
    })

    const totalNeeded = itemsWithUnits.reduce((sum, i) => sum + i.needed, 0)
    const totalAssigned = itemsWithUnits.reduce((sum, i) => sum + i.assigned, 0)

    return {
      items: itemsWithUnits,
      totalNeeded,
      totalAssigned,
      isReadyForDelivery: totalAssigned >= totalNeeded
    }
  } catch (error) {
    console.error('Error getting receipt inventory status:', error)
    return null
  }
}
// ==========================================
// PARTIAL DELIVERY ORDER CREATION
// ==========================================

// Create a delivery order for selected items from a receipt
// KEY FIX: Now stores actual inventory_unit_ids (UUIDs) instead of just SKU strings
export async function createPartialDeliveryOrder(receiptId, selectedSkus, storeId = null, selectedItemIds = []) {
  try {
    console.log('📦 Creating delivery order for receipt:', receiptId)
    console.log('📦 Selected SKUs:', selectedSkus)
    console.log('📦 Selected Item IDs:', selectedItemIds)
    
    // Get the receipt details
    const receipt = await fetchReceiptById(receiptId)
    if (!receipt) {
      throw new Error('Receipt not found')
    }

    // =========================================================
    // Delete any existing PENDING routes for this receipt
    // This ensures partial delivery selection overrides auto-created routes
    // =========================================================
    const { data: existingStops } = await supabase
      .from('delivery_route_stops')
      .select('id, route_id, status')
      .eq('receipt_id', receiptId)
      .in('status', ['pending', 'assigned'])
    
    if (existingStops && existingStops.length > 0) {
      console.log('🗑️ Found existing pending stops for receipt, cleaning up:', existingStops.length)
      
      // Get unique route IDs
      const routeIds = [...new Set(existingStops.map(s => s.route_id))]
      
      // Delete the stops first
      await supabase
        .from('delivery_route_stops')
        .delete()
        .eq('receipt_id', receiptId)
        .in('status', ['pending', 'assigned'])
      
      // Delete routes that now have no stops
      for (const routeId of routeIds) {
        const { data: remainingStops } = await supabase
          .from('delivery_route_stops')
          .select('id')
          .eq('route_id', routeId)
        
        if (!remainingStops || remainingStops.length === 0) {
          console.log('🗑️ Deleting empty route:', routeId)
          await supabase
            .from('delivery_routes')
            .delete()
            .eq('id', routeId)
        }
      }
      
      // Reset any inventory units that were marked out_for_delivery back to reserved
      const { error: resetError } = await supabase
        .from('inventory_units')
        .update({ status: 'reserved', updated_at: new Date().toISOString() })
        .eq('reserved_for_receipt_id', receiptId)
        .eq('status', 'out_for_delivery')
      
      if (resetError) {
        console.error('Error resetting inventory units:', resetError)
      } else {
        console.log('✅ Reset inventory units back to reserved')
      }
    }

    // =========================================================
    // FIX: Use selectedItemIds to precisely identify which items to deliver
    // This handles duplicate SKUs correctly!
    // =========================================================
    let itemsToDeliver = []
    
    if (selectedItemIds && selectedItemIds.length > 0) {
      // NEW: Match by receipt_item_id for precise selection
      itemsToDeliver = (receipt.items || []).filter(item => 
        selectedItemIds.includes(String(item.id))
      )
      console.log('📦 Items matched by ID:', itemsToDeliver.map(i => ({ id: i.id, sku: i.sku })))
    } else {
      // Fallback: Match by SKU (legacy behavior)
      itemsToDeliver = (receipt.items || []).filter(item => 
        selectedSkus.some(sku => sku.toLowerCase() === item.sku?.toLowerCase())
      )
      console.log('📦 Items matched by SKU (fallback):', itemsToDeliver.map(i => i.sku))
    }

    if (itemsToDeliver.length === 0) {
      throw new Error('No matching items found')
    }

    console.log('📦 Items to deliver:', itemsToDeliver.map(i => ({ id: i.id, sku: i.sku })))

    // =========================================================
    // FIX: Get inventory units by receipt_item_id (precise matching)
    // =========================================================
    const selectedItemIdStrings = itemsToDeliver.map(i => String(i.id))
    
    // Get units that have receipt_item_id matching our selected items
    const { data: unitsWithItemId, error: unitsError } = await supabase
      .from('inventory_units')
      .select('id, sku, status, unit_serial, receipt_item_id')
      .eq('reserved_for_receipt_id', receiptId)
      .in('status', ['reserved', 'at_store', 'in_warehouse'])
    
    if (unitsError) {
      console.error('Error fetching inventory units:', unitsError)
      throw new Error('Failed to fetch inventory units')
    }
    
    console.log('📦 Found inventory units for receipt:', unitsWithItemId?.length || 0)
    
    // Filter to only units that match our selected item IDs
    let unitsToDeliver = []
    
    if (selectedItemIds && selectedItemIds.length > 0) {
      // NEW: Match by receipt_item_id for precise partial delivery
      unitsToDeliver = (unitsWithItemId || []).filter(unit => 
        unit.receipt_item_id && selectedItemIdStrings.includes(String(unit.receipt_item_id))
      )
      console.log('📦 Units matched by receipt_item_id:', unitsToDeliver.map(u => ({ 
        id: u.id, 
        sku: u.sku, 
        receipt_item_id: u.receipt_item_id 
      })))
    } else {
      // Fallback: Match by SKU (legacy behavior)
      const selectedSkusLower = selectedSkus.map(s => s.toLowerCase().trim())
      unitsToDeliver = (unitsWithItemId || []).filter(unit => 
        selectedSkusLower.includes(unit.sku?.toLowerCase().trim())
      )
      console.log('📦 Units matched by SKU (fallback):', unitsToDeliver.length)
    }

    // Get the actual UUID IDs of the inventory units
    const inventoryUnitIds = unitsToDeliver.map(u => u.id)

    // =========================================================
    // UNITLESS ITEMS: selected items that have NO inventory unit.
    // These are items marked ready without stock (no-SKU items, or
    // SKU'd items where nothing was in inventory). They are tracked
    // purely via item_statuses, so they must ride the stop by
    // receipt_item_id rather than by inventory unit.
    // =========================================================
    const itemIdsWithUnit = new Set(
      unitsToDeliver
        .map(u => u.receipt_item_id)
        .filter(Boolean)
        .map(id => String(id))
    )
    const unitlessReceiptItemIds = itemsToDeliver
      .map(i => String(i.id))
      .filter(id => !itemIdsWithUnit.has(id))

    console.log('🎯 Units to deliver:', unitsToDeliver.length)
    console.log('🔑 Inventory unit IDs:', inventoryUnitIds)
    console.log('🏷️ Unitless receipt_item_ids (status-tracked):', unitlessReceiptItemIds)

    // Only a true error if NOTHING is going out — neither units nor
    // unitless items. A delivery of only unitless items is valid.
    if (inventoryUnitIds.length === 0 && unitlessReceiptItemIds.length === 0) {
      throw new Error('Selected items could not be found on this receipt.')
    }

    const today = new Date().toISOString().split('T')[0]
    
    // =========================================================
    // IMPROVED: Check if this is truly a partial delivery
    // Compare total receipt_items (rows) vs (already delivered + now delivering)
    // Each receipt_item row = 1 item for partial delivery tracking
    // =========================================================
    const totalReceiptItems = (receipt.items || []).length
    
    // Lines already with the customer. item_statuses is the record (one
    // row per receipt line, delivered OR picked up); legacy inventory units
    // are honored too, de-duplicated by receipt_item_id so no line counts
    // twice. Counting units alone called the LAST delivery of a receipt
    // "partial" whenever the earlier lines had been closed in the drawer.
    const { data: deliveredStatusRows } = await supabase
      .from('item_statuses')
      .select('receipt_item_id')
      .eq('receipt_id', receiptId)
      .in('status', ITEM_TERMINAL_STATUSES)
    const { data: deliveredUnits } = await supabase
      .from('inventory_units')
      .select('id, receipt_item_id')
      .eq('reserved_for_receipt_id', receiptId)
      .in('status', UNIT_TERMINAL_STATUSES)
    const deliveredLineIds = new Set(
      (deliveredStatusRows || []).map(r => r.receipt_item_id).filter(Boolean).map(String)
    )
    let legacyUnitsWithoutLine = 0
    for (const u of (deliveredUnits || [])) {
      if (u.receipt_item_id) deliveredLineIds.add(String(u.receipt_item_id))
      else legacyUnitsWithoutLine += 1
    }
    const alreadyDelivered = deliveredLineIds.size + legacyUnitsWithoutLine
    const deliveringNow = inventoryUnitIds.length + unitlessReceiptItemIds.length
    const afterThisDelivery = alreadyDelivered + deliveringNow
    
    console.log(`📊 Delivery check: ${totalReceiptItems} total, ${alreadyDelivered} already delivered, ${deliveringNow} delivering now (${inventoryUnitIds.length} units + ${unitlessReceiptItemIds.length} unitless)`)
    
    // It's a partial delivery if there will still be items remaining after this delivery
    const isPartial = afterThisDelivery < totalReceiptItems

    // Create delivery route
    const totalDeliveringCount = inventoryUnitIds.length + unitlessReceiptItemIds.length
    const routeName = isPartial 
      ? `Partial Delivery - ${receipt.customer_name} (${totalDeliveringCount} items)`
      : `Delivery - ${receipt.customer_name}`

    const { data: route, error: routeError } = await supabase
      .from('delivery_routes')
      .insert({
        store_id: storeId || receipt.store_id || null,
        route_name: routeName,
        delivery_date: today,
        stop_order: [receiptId],
        total_stops: 1,
        status: 'planned'
      })
      .select()
      .single()

    if (routeError) {
      console.error('Error creating route:', routeError)
      throw routeError
    }

    console.log('✅ Created route:', route.id)

    // =========================================================
    // KEY FIX: Store inventory_unit_ids in the stop (not just SKUs)
    // This is what DriverView.jsx looks for!
    // =========================================================
    const { data: stop, error: stopError } = await supabase
      .from('delivery_route_stops')
      .insert({
        route_id: route.id,
        receipt_id: receiptId,
        stop_number: 1,
        status: 'pending',
        address: receipt.delivery_address || receipt.customer_address,
        inventory_unit_ids: inventoryUnitIds,
        receipt_item_ids: unitlessReceiptItemIds,
        is_partial_delivery: isPartial,
        driver_notes: isPartial 
          ? `Partial delivery: ${totalDeliveringCount} of ${(receipt.items || []).length} item(s)`
          : null,
        selected_items: itemsToDeliver.map(item => ({
          sku: item.sku,
          item_name: item.item_name,
          color: item.color,
          quantity: item.quantity
        }))
      })
      .select()
      .single()

    if (stopError) {
      console.error('Error creating stop:', stopError)
      // Clean up the route we created
      await supabase.from('delivery_routes').delete().eq('id', route.id)
      throw stopError
    }

    console.log('✅ Created stop:', stop.id, 'with inventory_unit_ids:', inventoryUnitIds)

    // NOTE: Inventory units stay as 'reserved' until route is dispatched
    // The dispatchRoute function will update them to 'out_for_delivery'
    console.log('📦 Inventory units remain as reserved until dispatch')

    // Update item_statuses for UI display - show as "Ready for Delivery"
    // Keyed on receipt_item_id so it works for unitless / no-SKU items too.
    // item_statuses requires item_name (NOT NULL).
    for (const item of itemsToDeliver) {
      const { data: existingStatus } = await supabase
        .from('item_statuses')
        .select('id')
        .eq('receipt_id', receiptId)
        .eq('receipt_item_id', item.id)
        .maybeSingle()

      if (existingStatus) {
        await supabase
          .from('item_statuses')
          .update({
            status: 'at_store',
            notes: `Ready for delivery - ${new Date().toLocaleDateString()}`,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingStatus.id)
      } else {
        await supabase
          .from('item_statuses')
          .insert({
            receipt_id: receiptId,
            receipt_item_id: item.id,
            item_name: item.item_name || item.sku || 'Item',
            sku: item.sku || null,
            status: 'at_store',
            notes: `Ready for delivery - ${new Date().toLocaleDateString()}`,
            updated_at: new Date().toISOString()
          })
      }
    }

    // NOTE: Receipt status stays as-is until route is DISPATCHED
    // The dispatchRoute function in DeliveriesTab will set 'out_for_delivery'
    // This keeps the flow clean: pending → out_for_delivery → delivered
    console.log('📋 Receipt status unchanged - will update when route is dispatched')

    return {
      success: true,
      route: route,
      stop: stop,
      itemCount: totalDeliveringCount,
      inventoryUnitIds: inventoryUnitIds,  // Return for debugging
      unitlessReceiptItemIds: unitlessReceiptItemIds,
      isPartial
    }
  } catch (error) {
    console.error('Error creating delivery order:', error)
    return {
      success: false,
      error: error.message
    }
  }
}

// Complete a delivery stop (called when driver signs)
// KEY FIX: Now uses inventory_unit_ids instead of selected_items
export async function completePartialDeliveryStop(stopId, receiptId, inventoryUnitIds = null) {
  try {
    console.log('📦 Completing delivery stop:', stopId, 'Receipt:', receiptId, 'Unit IDs:', inventoryUnitIds)

    // Always fetch the stop: we need receipt_item_ids (unitless items —
    // tracked purely in item_statuses since inventory was retired) even
    // when the caller passed inventoryUnitIds.
    const { data: stopRow } = await supabase
      .from('delivery_route_stops')
      .select('inventory_unit_ids, receipt_item_ids')
      .eq('id', stopId)
      .single()

    if (!inventoryUnitIds || inventoryUnitIds.length === 0) {
      inventoryUnitIds = stopRow?.inventory_unit_ids || []
    }
    const unitlessItemIds = stopRow?.receipt_item_ids || []

    console.log('📦 Marking as delivered — units:', inventoryUnitIds, 'unitless items:', unitlessItemIds)

    // Only a true error if the stop is carrying NOTHING — a stop made
    // entirely of unitless (status-tracked) items is valid and common.
    if (inventoryUnitIds.length === 0 && unitlessItemIds.length === 0) {
      console.warn('⚠️ Stop has no inventory units and no receipt item ids - cannot complete')
      return {
        success: false,
        error: 'No items found on this delivery stop'
      }
    }

    // Mark the specific inventory units as delivered (legacy stock only —
    // skipped entirely for unitless deliveries).
    let deliveredUnits = []
    if (inventoryUnitIds.length > 0) {
      const { data: unitRows, error: deliverError } = await supabase
        .from('inventory_units')
        .update({
          status: 'delivered',
          location: 'customer',
          delivered_at: new Date().toISOString()
        })
        .in('id', inventoryUnitIds)
        .select('id, sku, receipt_item_id')

      if (deliverError) {
        console.error('Error delivering units:', deliverError)
        throw deliverError
      }

      deliveredUnits = unitRows || []
      console.log('✅ Delivered', deliveredUnits.length, 'units')

      // Update item_statuses for each delivered unit
      for (const unit of deliveredUnits) {
        if (unit.receipt_item_id) {
          await supabase
            .from('item_statuses')
            .upsert({
              receipt_id: receiptId,
              receipt_item_id: unit.receipt_item_id,
              sku: unit.sku,
              status: 'delivered',
              notes: `Delivered on ${new Date().toLocaleDateString()}`,
              updated_at: new Date().toISOString()
            }, { onConflict: 'receipt_id,receipt_item_id' })
        } else {
          // Legacy fallback for units without receipt_item_id
          await supabase
            .from('item_statuses')
            .update({
              status: 'delivered',
              notes: `Delivered on ${new Date().toLocaleDateString()}`,
              updated_at: new Date().toISOString()
            })
            .eq('receipt_id', receiptId)
            .ilike('sku', unit.sku)
        }
      }
    }

    // UNITLESS ITEMS: complete the status-tracked items riding this stop.
    // These never had inventory; item_statuses is their source of truth.
    if (unitlessItemIds.length > 0) {
      const { data: unitlessRows, error: unitlessErr } = await supabase
        .from('receipt_items')
        .select('id, sku, item_name')
        .in('id', unitlessItemIds)

      if (unitlessErr) {
        console.error('Error fetching unitless receipt items:', unitlessErr)
      }

      for (const row of (unitlessRows || [])) {
        await supabase
          .from('item_statuses')
          .upsert({
            receipt_id: receiptId,
            receipt_item_id: row.id,
            sku: row.sku || null,
            item_name: row.item_name || row.sku || 'Item',
            status: 'delivered',
            notes: `Delivered on ${new Date().toLocaleDateString()}`,
            updated_at: new Date().toISOString()
          }, { onConflict: 'receipt_id,receipt_item_id' })
      }

      console.log('✅ Completed', (unitlessRows || []).length, 'unitless item(s) via item_statuses')
    }

    // Update the stop status
    await supabase
      .from('delivery_route_stops')
      .update({
        status: 'delivered',
        completed_at: new Date().toISOString()
      })
      .eq('id', stopId)

    // Check if ALL items on the receipt are now delivered
    const deliveryStatus = await checkAllItemsDelivered(receiptId)

    if (deliveryStatus.allDelivered) {
      await supabase
        .from('receipts')
        .update({ 
          delivery_status: 'delivered',
          survey_status: 'pending',
          updated_at: new Date().toISOString()
        })
        .eq('id', receiptId)
      
      console.log('✅ All items delivered - receipt marked as delivered')
      
      // TRIGGER SURVEY SMS IMMEDIATELY
      try {
        console.log('🎯 Triggering customer survey for receipt:', receiptId)
        const surveyResponse = await fetch('/api/chat-survey', {
          method: 'POST',
          headers: await authHeaders(),
          body: JSON.stringify({
            receipt_id: receiptId,
            conversation_stage: 'start_survey'
          })
        })
        const surveyResult = await surveyResponse.json()
        if (surveyResult.success) {
          console.log('✅ Survey SMS sent successfully')
        } else {
          console.error('❌ Survey trigger failed:', surveyResult.error)
        }
      } catch (surveyError) {
        console.error('❌ Error triggering survey:', surveyError)
      }
    } else {
      await supabase
        .from('receipts')
        .update({ 
          delivery_status: 'partially_delivered',
          updated_at: new Date().toISOString()
        })
        .eq('id', receiptId)
      
      console.log(`📦 Partial delivery complete - ${deliveryStatus.remainingCount} items remaining`)
    }

    return {
      success: true,
      allDelivered: deliveryStatus.allDelivered,
      deliveredCount: (deliveredUnits?.length || 0) + unitlessItemIds.length,
      remainingCount: deliveryStatus.remainingCount
    }
  } catch (error) {
    console.error('Error completing delivery:', error)
    return {
      success: false,
      error: error.message
    }
  }
}